import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { customerService } from '../../services/customerService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import GridPager from '../../components/GridPager';
import { FilterPopover } from '../../components/gridFilters';
import { cell, GRID } from '../../styles/gridStyles';
import { makeCustomerSpec, exportCustomerTemplate, parseCustomerTemplate } from '../../utils/customerExcel';

// =============================================================================
// CustomerMasterPage
// Full-page editor for customer master data, built on the shared MantleGrid —
// the same entry feel as the Sales / Production volume screens. Parameterised
// by `kind`:
//   * 'groups'    -> md_customer_groups (code, name, active)
//   * 'customers' -> md_customers       (code, name, customer group, active)
//
// The key column (code) is fixed once a row exists; the name edits inline, the
// Active flag toggles on click, and (for customers) the customer group is
// picked from a small modal. A toolbar Import/Export dialog round-trips an Excel
// template with an overwrite / replace mode — mirroring the volume uploads.
// =============================================================================

const IMPORT_MODES = [
  { id: 'overwrite', label: 'Overwrite', hint: 'Update matching records by code, and add any new ones.' },
  { id: 'replace', label: 'Replace all', hint: 'Wipe every existing record first, then load the file.' },
];

const inputClass =
  'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

// ---- Searchable picker (type-ahead) for the customer group ------------------
const SearchSelect = ({ options, value, onChange, codeKey, descKey, placeholder, allowClear = false }) => {
  const labelMap = useMemo(
    () => Object.fromEntries(options.map((o) => [o[codeKey], o[descKey]])),
    [options, codeKey, descKey],
  );
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  const display = value ? `${value}${labelMap[value] ? ' — ' + labelMap[value] : ''}` : '';

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter((o) =>
          String(o[codeKey]).toLowerCase().includes(q) || String(o[descKey] || '').toLowerCase().includes(q))
      : options;
    return list.slice(0, 50);
  }, [options, query, codeKey, descKey]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const choose = (o) => { onChange(o ? o[codeKey] : ''); setQuery(''); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        className={inputClass}
        placeholder={placeholder}
        value={open ? query : display}
        onFocus={() => { setQuery(''); setHi(0); setOpen(true); }}
        onChange={(e) => { setQuery(e.target.value); setHi(0); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (matches[hi]) choose(matches[hi]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg text-sm">
          {allowClear && value && (
            <li onMouseDown={(e) => { e.preventDefault(); choose(null); }}
              className="px-3 py-1.5 cursor-pointer text-slate-400 italic hover:bg-slate-100 dark:hover:bg-slate-700">
              (None)
            </li>
          )}
          {matches.length === 0 && <li className="px-3 py-2 text-slate-400">No matches</li>}
          {matches.map((o, i) => (
            <li key={o[codeKey]}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}
              onMouseEnter={() => setHi(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === hi ? 'bg-slate-100 dark:bg-slate-700' : ''}`}>
              <span className="font-medium text-slate-800 dark:text-slate-100">{o[codeKey]}</span>
              {o[descKey] && <span className="text-slate-500 dark:text-slate-400"> — {o[descKey]}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---- Add-row modal ----------------------------------------------------------
const AddRowModal = ({ kind, groups, existingKeys, onAdd, onClose }) => {
  const isCustomers = kind === 'customers';
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [group, setGroup] = useState('');
  const [active, setActive] = useState(true);

  const dup = existingKeys.has(code.trim());
  const problem = !code.trim() ? `${isCustomers ? 'Customer' : 'Group'} code is required.`
    : dup ? 'That code already exists.'
    : !name.trim() ? `${isCustomers ? 'Customer' : 'Group'} name is required.` : '';
  const canAdd = !problem;

  const submit = () => {
    if (!canAdd) return;
    onAdd(isCustomers
      ? { customer_code: code.trim(), customer_name: name.trim(), customer_group_code: group || null, is_active: active }
      : { customer_group_code: code.trim(), group_name: name.trim(), is_active: active });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add {isCustomers ? 'customer' : 'customer group'}</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {isCustomers ? 'Enter a code and name, and optionally assign a customer group.' : 'Enter a code and name for the group.'}
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            {isCustomers ? 'Customer Code' : 'Group Code'}<span className="text-red-600 dark:text-red-400"> (required)</span>
            <input className={inputClass} value={code} onChange={(e) => setCode(e.target.value)} autoFocus
              placeholder={isCustomers ? 'e.g. CUST-001' : 'e.g. GRP-RETAIL'} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            {isCustomers ? 'Customer Name' : 'Group Name'}<span className="text-red-600 dark:text-red-400"> (required)</span>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)}
              placeholder={isCustomers ? 'e.g. Shoprite Checkers' : 'e.g. Major Retail'} />
          </label>
          {isCustomers && (
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Customer Group (optional)
              <SearchSelect
                options={groups}
                value={group}
                onChange={(c) => setGroup(c || '')}
                codeKey="customer_group_code"
                descKey="group_name"
                allowClear
                placeholder="Type to search customer groups…"
              />
            </label>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 mt-1">
            <input type="checkbox" className="accent-slate-900" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>

          <p className="m-0 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem}</p>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={!canAdd}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}>
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Group-pick modal (customers only) --------------------------------------
const GroupPickModal = ({ groups, current, onPick, onClose }) => {
  const [group, setGroup] = useState(current || '');
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Set customer group</h3>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 flex flex-col gap-3">
          <SearchSelect
            options={groups}
            value={group}
            onChange={(c) => setGroup(c || '')}
            codeKey="customer_group_code"
            descKey="group_name"
            allowClear
            placeholder="Type to search customer groups…"
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Cancel
            </button>
            <button type="button" onClick={() => onPick(group || null)}
              className="px-4 py-2 text-sm font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700">
              Set group
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Import / Export modal --------------------------------------------------
const PortModal = ({ title, mode, setMode, isPorting, importErrors, onExport, onImport, onClose }) => (
  <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
    onMouseDown={(e) => { if (e.target === e.currentTarget && !isPorting) onClose(); }}>
    <div className="w-full max-w-2xl rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Import / Export — {title}</h3>
        <button type="button" onClick={onClose} disabled={isPorting}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5">
        <h4 className="text-xs font-semibold text-slate-900 dark:text-slate-100 mb-2 uppercase tracking-wide">On import, existing records should be…</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {IMPORT_MODES.map((m) => (
            <label key={m.id}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                mode === m.id ? 'border-slate-900 bg-slate-50 dark:bg-slate-700' : 'border-slate-200 dark:border-slate-600 hover:border-slate-300'
              }`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="portMode" className="accent-slate-900" checked={mode === m.id} onChange={() => setMode(m.id)} />
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{m.label}</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">{m.hint}</p>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Export Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Excel pre-filled with the current records. Dropdown columns carry validation lists.
            </p>
            <button onClick={onExport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 disabled:opacity-50">
              {isPorting ? 'Working…' : 'Export'}
            </button>
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Import Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Upload a completed template. Every row is validated; if anything is off, nothing is saved.
            </p>
            <button onClick={onImport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
              {isPorting ? 'Working…' : `Import (${IMPORT_MODES.find((m) => m.id === mode).label})`}
            </button>
          </div>
        </div>

        {importErrors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-700 mb-1.5">{importErrors.length} row(s) need attention — nothing was imported:</p>
            <ul className="text-xs text-red-600 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
              {importErrors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ---- Page -------------------------------------------------------------------
const CustomerMasterPage = ({ kind = 'groups' }) => {
  const isCustomers = kind === 'customers';
  const spec = useMemo(() => makeCustomerSpec(kind), [kind]);
  const keyField = spec.keyField;

  const [rows, setRows] = useState([]);      // { key, ...fields, isNew }
  const [dirty, setDirty] = useState({});    // { key: true }
  const [groups, setGroups] = useState([]);  // reference list (customers only)
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Server-side filtering and paging (see components/GridPager.jsx).
  const [filters, setFilters] = useState({});
  const [filterUI, setFilterUI] = useState(null);
  const [pageInfo, setPageInfo] = useState({ total: null, hasMore: false });
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(100);
  const activeFilterIds = useMemo(() => Object.keys(filters), [filters]);
  const setFilter = useCallback((colId, f) => {
    setOffset(0);
    setFilters((prev) => {
      const next = { ...prev };
      if (!f || (!f.text && !f.values)) delete next[colId];
      else next[colId] = f;
      return next;
    });
  }, []);

  const [addOpen, setAddOpen] = useState(false);
  const [groupPickFor, setGroupPickFor] = useState(null); // row key or null
  const [portOpen, setPortOpen] = useState(false);
  const [portMode, setPortMode] = useState('overwrite');
  const [isPorting, setIsPorting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);

  const groupNameOf = useCallback((code) => groups.find((g) => g.customer_group_code === code)?.group_name || '', [groups]);

  // ---- Load ----------------------------------------------------------------
  const loadGroupsRef = useCallback(async () => {
    const r = await customerService.getGroups();
    if (r.success) setGroups(r.data);
  }, []);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Groups are a short catalogue and stay unpaged; customers are the ones
      // that scale, so only they ask for a page.
      const res = isCustomers
        ? await customerService.getCustomers({ filters, limit, offset })
        : await customerService.getGroups();
      const data = res.success ? res.data : [];
      setRows(data.map((r) => ({ ...r, key: r[keyField], isNew: false })));
      setPageInfo(isCustomers
        ? { total: res.total ?? null, hasMore: !!res.hasMore }
        : { total: null, hasMore: false });
      setDirty({});
    } catch (e) {
      setError('Failed to load records.');
      setRows([]);
      setPageInfo({ total: null, hasMore: false });
    } finally {
      setIsLoading(false);
    }
  }, [isCustomers, keyField, filters, limit, offset]);

  useEffect(() => {
    loadRows();
    if (isCustomers) loadGroupsRef();
  }, [loadRows, isCustomers, loadGroupsRef]);

  // ---- Grid model ----------------------------------------------------------
  // hasMenu puts a ▾ on every data column; the adapter forwards the click as
  // onHeaderMenuClick. Filtering runs on the server — a customer master can run
  // to tens of thousands of rows, and the grid now holds only one page.
  const columns = useMemo(() => {
    const cols = spec.columns.map((col, i) => {
      const width = col.active ? 96 : col.list ? 240 : i === 0 ? 200 : 300;
      return { columnId: col.key, width, resizable: true, hasMenu: true };
    });
    return [...cols, { columnId: 'del', width: 40, resizable: false }];
  }, [spec]);

  // Only the group columns have a small enough domain to offer a checklist —
  // the group list is already loaded for the row editor. For customer code and
  // name the popover's contains-box is the tool, and an empty checklist is the
  // honest rendering of "too many values to list".
  const onHeaderMenu = useCallback((columnId, bounds) => {
    if (!spec.columns.some((c) => c.key === columnId)) return;
    const options = columnId === 'customer_group_code'
      ? [...new Set((groups || []).map((g) => String(g.customer_group_code ?? '')))].sort()
      : [];
    setFilterUI({
      colId: columnId,
      options,
      x: bounds ? bounds.x : 200,
      y: bounds ? bounds.y + bounds.height : 200,
    });
  }, [spec, groups]);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [...spec.columns.map((col) => cell.header(col.header)), cell.header('')],
    };

    const dataRows = rows.map((r, idx) => ({
      rowId: r.key,
      height: GRID.size.dataRow,
      cells: [
        ...spec.columns.map((col, ci) => {
          if (col.active) {
            return cell.tag(r[col.key] === false ? 'Inactive' : 'Active', { active: r[col.key] !== false });
          }
          if (col.list) { // customer group — click to edit
            const code = r[col.key];
            const label = code ? `${code}${groupNameOf(code) ? ' · ' + groupNameOf(code) : (r.group_name ? ' · ' + r.group_name : '')}` : '—';
            return cell.text(label, { idx, color: code ? GRID.color.text : GRID.color.muted });
          }
          if (ci === 0) return cell.label(r[col.key], { idx }); // code (key)
          return cell.editText(r[col.key] ?? '', { idx }); // name
        }),
        cell.text('✕', { idx, color: GRID.color.muted }),
      ],
    }));

    return [headerRow, ...dataRows];
  }, [rows, spec, groupNameOf]);

  // ---- Edits ---------------------------------------------------------------
  const markDirty = (key) => setDirty((prev) => ({ ...prev, [key]: true }));

  const handleChanges = (changes) => {
    setRows((prev) => {
      let touched = false;
      const next = prev.map((r) => ({ ...r }));
      for (const ch of changes) {
        const col = String(ch.columnId);
        const row = next.find((r) => r.key === ch.rowId);
        if (!row) continue;
        // Only the name column is inline-editable text.
        const nameCol = spec.columns.find((c) => !c.active && !c.list && c.key !== keyField);
        if (nameCol && col === nameCol.key) {
          row[nameCol.key] = ch.newCell?.text ?? '';
          markDirty(row.key);
          touched = true;
        }
      }
      return touched ? next : prev;
    });
  };

  // Clicks: ✕ deletes; Active toggles; the group cell opens the pick modal.
  const handleFocus = (loc) => {
    const rowId = String(loc.rowId);
    if (rowId === 'header') return true;
    const col = String(loc.columnId);
    const row = rows.find((r) => r.key === rowId);
    if (!row) return true;

    if (col === 'del') { deleteRow(row); return false; }

    const activeCol = spec.columns.find((c) => c.active);
    if (activeCol && col === activeCol.key) {
      setRows((prev) => prev.map((r) => (r.key === rowId ? { ...r, [activeCol.key]: r[activeCol.key] === false } : r)));
      markDirty(rowId);
      return false;
    }

    const listCol = spec.columns.find((c) => c.list);
    if (listCol && col === listCol.key) { setGroupPickFor(rowId); return false; }

    return true;
  };

  // ---- Add row -------------------------------------------------------------
  const existingKeys = useMemo(() => new Set(rows.map((r) => r.key)), [rows]);

  const confirmAdd = (rec) => {
    const key = rec[keyField];
    if (existingKeys.has(key)) return;
    setRows((prev) => [{ ...rec, key, isNew: true }, ...prev]);
    markDirty(key);
    setAddOpen(false);
  };

  // ---- Group pick (customers) ----------------------------------------------
  const applyGroup = (code) => {
    setRows((prev) => prev.map((r) => (r.key === groupPickFor
      ? { ...r, customer_group_code: code, group_name: groupNameOf(code) }
      : r)));
    markDirty(groupPickFor);
    setGroupPickFor(null);
  };

  // ---- Delete row ----------------------------------------------------------
  const deleteRow = async (row) => {
    if (row.isNew) {
      setRows((prev) => prev.filter((r) => r.key !== row.key));
      setDirty((prev) => { const n = { ...prev }; delete n[row.key]; return n; });
      return;
    }
    if (!window.confirm(`Delete ${isCustomers ? 'customer' : 'group'} "${row.key}"?`)) return;
    setError(null);
    try {
      if (isCustomers) await customerService.deleteCustomers([row.key]);
      else await customerService.deleteGroups([row.key]);
      await loadRows();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete record.');
    }
  };

  // ---- Save ----------------------------------------------------------------
  const dirtyRowPayload = (r) => (isCustomers
    ? { customer_code: r.customer_code, customer_name: r.customer_name, customer_group_code: r.customer_group_code || null, is_active: r.is_active !== false }
    : { customer_group_code: r.customer_group_code, group_name: r.group_name, is_active: r.is_active !== false });

  const handleSave = async () => {
    setError(null);
    const payloadRows = rows.filter((r) => dirty[r.key]).map(dirtyRowPayload);
    if (payloadRows.length === 0) return;
    setIsSaving(true);
    try {
      const payload = { mode: 'overwrite', rows: payloadRows };
      const res = isCustomers
        ? await customerService.upsertCustomers(payload)
        : await customerService.upsertGroups(payload);
      if (!res.success) throw new Error(res.message);
      await loadRows();
      if (isCustomers) await loadGroupsRef();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save records.');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Import / Export -----------------------------------------------------
  const handleExport = async () => {
    setImportErrors([]); setIsPorting(true);
    try {
      await exportCustomerTemplate({ kind, rows, groups });
    } catch { setError('Failed to generate the Excel template.'); }
    finally { setIsPorting(false); }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportErrors([]); setError(null); setIsPorting(true);
    try {
      const { rows: parsed, errors } = await parseCustomerTemplate(file, kind, groups);
      if (errors.length) { setImportErrors(errors); return; }
      if (parsed.length === 0 && portMode !== 'replace') { setError('No data rows found in the template.'); return; }
      const payload = { mode: portMode, rows: parsed };
      const res = isCustomers
        ? await customerService.upsertCustomers(payload)
        : await customerService.upsertGroups(payload);
      if (!res.success) throw new Error(res.message);
      setPortOpen(false);
      await loadRows();
      if (isCustomers) await loadGroupsRef();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to read or import the template.');
    } finally { setIsPorting(false); }
  };

  const hasChanges = Object.keys(dirty).length > 0;
  const title = isCustomers ? 'Customers' : 'Customer Groups';
  const subtitle = isCustomers
    ? 'The customer master — code, name and the group each belongs to. These become the customer / group dimensions on the sales-volume screen.'
    : 'Channel / key-account groupings (e.g. Major Retail, Wholesale). Load these before customers, which reference their group.';

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="flex-1 min-h-0">
          <MantleGrid
            rows={gridRows}
            columns={columns}
            isLoading={isLoading}
            onCellsChanged={handleChanges}
            onFocusLocationChanging={handleFocus}
            onHeaderMenuClick={onHeaderMenu}
            hint={isCustomers
              ? 'Edit the name inline. Click the Active cell to toggle it, or the Customer Group cell to reassign. Header ▾ filters — the filter runs on the server, so it searches every customer, not just this page. ✕ removes a row.'
              : 'Edit the name inline. Click the Active cell to toggle it. ✕ removes a row.'}
            toolbar={
              <>
                {activeFilterIds.length > 0 && (
                  <MantleGridButton onClick={() => { setFilters({}); setOffset(0); }}>
                    Clear filters ({activeFilterIds.length})
                  </MantleGridButton>
                )}
                <MantleGridButton onClick={() => setAddOpen(true)}>+ Add {isCustomers ? 'customer' : 'group'}</MantleGridButton>
                <MantleGridButton onClick={() => { setImportErrors([]); setPortOpen(true); }}>Import / Export</MantleGridButton>
                <MantleGridButton onClick={handleSave} disabled={isSaving || !hasChanges} accent={hasChanges}>
                  {isSaving ? 'Saving…' : 'Save'}
                </MantleGridButton>
              </>
            }
          />
          </div>
          {isCustomers && (
            <GridPager
              offset={offset}
              limit={limit}
              rowCount={rows.length}
              total={pageInfo.total}
              hasMore={pageInfo.hasMore}
              loading={isLoading}
              onOffset={setOffset}
              onLimit={(n) => { setLimit(n); setOffset(0); }}
            />
          )}
        </div>
      </div>

      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}

      {addOpen && (
        <AddRowModal
          kind={kind}
          groups={groups}
          existingKeys={existingKeys}
          onAdd={confirmAdd}
          onClose={() => setAddOpen(false)}
        />
      )}

      {groupPickFor && (
        <GroupPickModal
          groups={groups}
          current={rows.find((r) => r.key === groupPickFor)?.customer_group_code || ''}
          onPick={applyGroup}
          onClose={() => setGroupPickFor(null)}
        />
      )}

      {portOpen && (
        <>
          <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileSelected} />
          <PortModal
            title={title}
            mode={portMode}
            setMode={setPortMode}
            isPorting={isPorting}
            importErrors={importErrors}
            onExport={handleExport}
            onImport={() => fileInputRef.current?.click()}
            onClose={() => { if (!isPorting) setPortOpen(false); }}
          />
        </>
      )}
    </div>
  );
};

export default CustomerMasterPage;
